"use client";

import {useEffect, useRef, useState} from "react";
import {PageLoader} from "@/src/components/Loaders";
import useMutableSearchParams from "@/src/hooks/useMutableSearchParams";
import {PROCESS_INSTANCE_QUERY} from "@/src/shared/const";
import {notFound} from "next/navigation";
import {useProcessSecurity} from "@/src/hooks/useProcessSecurity";
import {getGA} from "@/src/shared/utils/getGA";

interface CreateProcessInstanceProps {
    productSlug: string;
    searchParams: Record<string, any>;
}

export default function CreateProcessInstance({
                                                  productSlug,
                                                  searchParams,
                                              }: CreateProcessInstanceProps) {
    const didCall = useRef(false);
    const { setSearchParams } = useMutableSearchParams();
    const [error, setError] = useState<string | null>(null);
    const { createSession } = useProcessSecurity(null);

    useEffect(() => {
        if (didCall.current) return;
        didCall.current = true;

        async function createInstance() {
            try {
                const params = await getGA();

                const response = await fetch("/api/process-instance", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        productSlug,
                        utmSource: searchParams?.utm_source,
                        utmMedium: searchParams?.utm_medium,
                        utmCampaign: searchParams?.utm_campaign,
                        utmContent: searchParams?.utm_content,
                        utmTerm: searchParams?.utm_term,
                        gaClientId: params.client_id,
                        gaSessionId: params.session_id
                    }),
                });
                if (response.ok) {
                    const data = await response.json();
                    const processInstanceId = data.processInstance.id;

                    const sessionCreated = createSession(processInstanceId);
                    if (!sessionCreated) {
                        console.error("Failed to create secure session for process:", processInstanceId);
                    } else {
                        console.log("✅ Secure session created for process:", processInstanceId);
                    }

                    setSearchParams(PROCESS_INSTANCE_QUERY, processInstanceId, "replace");
                } else {
                    console.error("Error creating process instance:", response.status);
                    setError("Error creating process instance");
                }
            } catch (err) {
                console.error("Error during creation:", err);
                setError("Error creating process instance");
            }
        }

        createInstance();
    }, [productSlug, searchParams, createSession]);

    if (error) {
        return notFound()
    }

    return (
        <div className="flex h-[calc(100vh_-_344px)] w-full flex-1 items-center justify-center">
            <PageLoader/>
        </div>
    );
}
